/**
 * Pure session-state rules shared by the sync adapters and the hook.
 *
 * Kept free of imports so they can be tested without a browser, Firebase or
 * Vite's environment.
 */

/** Restores the complete deck shape Firebase does not preserve for empty arrays. */
function readGroupDeckSelection(value) {
  if (value?.mode !== 'manual') return null;

  const googlePlaceIds = Object.fromEntries(
    Object.entries(value.googlePlaceIds ?? {}).filter(
      ([, placeId]) => typeof placeId === 'string' && placeId.trim() !== ''
    )
  );

  return {
    mode: 'manual',
    restaurantIds: Array.isArray(value.restaurantIds)
      ? value.restaurantIds.filter((id) => typeof id === 'number' && Number.isFinite(id))
      : [],
    promotedIds: Array.isArray(value.promotedIds)
      ? value.promotedIds.filter((id) => typeof id === 'string')
      : [],
    ...(Object.keys(googlePlaceIds).length > 0 ? { googlePlaceIds } : {}),
  };
}

/**
 * Normalises a raw session node, filling fields older sessions never wrote.
 * Mirrors mobile/src/features/group/sync/index.ts.
 *
 * @param {Partial<GroupState> | null | undefined} value
 * @returns {GroupState}
 */
export function readGroupState(value) {
  return {
    members: value?.members ?? {},
    votes: value?.votes ?? {},
    times: value?.times ?? {},
    deck: readGroupDeckSelection(value?.deck),
    invites: value?.invites ?? {},
    submitted: value?.submitted ?? {},
    ended: value?.ended ?? null,
    places: value?.places ?? {},
  };
}

/**
 * The part of a restaurant a session stores, so a guest whose own catalogue
 * lacks a voted place can still see it in results. Nulls rather than
 * undefined, which Firebase refuses to write.
 *
 * @param {import('../../../lib/types.js').Restaurant} restaurant
 * @returns {PlaceSummary}
 */
export function placeSummary(restaurant) {
  return {
    id: restaurant.id,
    name: restaurant.name,
    cuisine: restaurant.cuisine ?? '',
    neighborhood: restaurant.neighborhood ?? '',
    price: restaurant.price ?? '$$',
    rating: restaurant.rating ?? 0,
    imageSeed: restaurant.imageSeed ?? `crave-place-${restaurant.id}`,
    coords: restaurant.coords ?? null,
    reservationUrl: restaurant.reservationUrl ?? null,
    instagramUrl: restaurant.instagramUrl ?? null,
  };
}

/**
 * Widens a stored place summary back to the restaurant shape the results UI
 * renders. Mirrors mobile/src/features/group/sync/index.ts.
 *
 * @param {PlaceSummary} place
 * @returns {import('../../../lib/types.js').Restaurant}
 */
export function restaurantFromPlace(place) {
  return {
    description: '',
    times: [],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: [],
    dietary: [],
    service: [],
    goodForGroups: false,
    past: false,
    upcoming: false,
    ...place,
  };
}

/**
 * Where a session stands on sharing votes. It only finishes itself once there
 * is a group to finish — a host alone who shares before anyone joins would
 * otherwise close it on the people they are about to invite.
 *
 * @param {GroupState} state
 */
export function groupProgress(state) {
  const memberIds = Object.keys(state.members);
  const submittedCount = memberIds.filter((id) => state.submitted[id] != null).length;
  return {
    memberCount: memberIds.length,
    submittedCount,
    allSubmitted: memberIds.length > 1 && submittedCount === memberIds.length,
    ended: state.ended != null,
  };
}

/**
 * Counts every live right swipe. Completion is tracked separately in
 * `submitted`; it must never hide a vote that has already been cast.
 *
 * @param {GroupState} state
 * @returns {Record<string, number>}
 */
export function liveVoteCounts(state) {
  const counts = {};
  for (const memberVotes of Object.values(state.votes)) {
    for (const [restaurantId, direction] of Object.entries(memberVotes ?? {})) {
      if (direction !== 'right') continue;
      counts[restaurantId] = (counts[restaurantId] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * @typedef {object} GroupDeckSelection
 * @property {'manual'} mode
 * @property {number[]} restaurantIds
 * @property {string[]} promotedIds
 * @property {Record<string, string>} [googlePlaceIds] Synthetic restaurant id to Google Place ID.
 */

/**
 * How long a session stays joinable after its first member arrived. A week
 * comfortably covers planning ahead for an upcoming outing, while still
 * eventually expiring a stale code so a recycled `CRAVE-1234` can't drop a
 * guest into an old session. Tune here.
 */
export const GROUP_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether a fetched session can still be joined: it must exist, still have at
 * least one member (an empty node means everyone left), and its oldest member
 * must have joined within the TTL window.
 *
 * @param {GroupState | null | undefined} state
 * @returns {boolean}
 */
export function isSessionJoinable(state) {
  if (state?.ended) return false;
  const members = state?.members ?? {};
  const joinTimes = Object.values(members)
    .map((member) => member?.joinedAt)
    .filter((joinedAt) => typeof joinedAt === 'number');

  if (joinTimes.length === 0) return false;
  return Date.now() - Math.min(...joinTimes) <= GROUP_SESSION_TTL_MS;
}
